import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { ArrowLeft, UserCog, Truck, Loader2, Search, FolderInput, Check, Trash2, Plus, Download, Columns3, AlertTriangle, ChevronDown, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ImportPreviewModal } from '@/components/common/ImportPreviewModal';
import { Input } from '@/components/ui/Input';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { DeleteConfirmDrawer } from '@/components/common/DeleteConfirmDrawer';
import { VehicleCell, DriverCell, CAN_ASSIGN, type PickableVehicle, type PickableDriver }
  from '@/components/common/VehicleCell';
import { useAuth } from '@/lib/auth';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { DriverForm } from '@/pages/drivers/DriverForm';
import { VehicleForm } from '@/pages/vehicles/VehicleForm';
import * as XLSX from 'xlsx';

/**
 * Filing screen: which side is each driver and each truck on, and move them.
 *
 * One screen for both, because the question is the same one twice and the
 * answer has to agree - a truck on NEOM whose driver is filed under Dumpers is
 * the exact confusion this exists to end.
 *
 * Everything is scoped by `projectScope`, including `none`, which is the whole
 * point: rows nobody has filed are the ones needing attention, and until now
 * there was no way to ask for them.
 */

type Kind = 'drivers' | 'vehicles';

interface SideDef { key: string; code: string; label: string }

// The three sides, plus the two pseudo-scopes the API understands. Codes match
// operational_projects; the scope tokens are what lib/project-scope resolves.
const SIDES: SideDef[] = [
  { key: 'neom', code: 'PRJ-NEOM', label: 'NEOM' },
  { key: 'riyadh_water', code: 'PRJ-RIYADH-WATER', label: 'Riyadh Water' },
  { key: 'dumpers', code: 'PRJ-DUMPERS', label: 'Dumpers' },
  { key: 'flatbed', code: 'PRJ-RIYADH', label: 'Riyadh Flatbed' },
];

interface Row {
  id: string;
  code: string;
  name: string;
  sub: string | null;
  projectId: string | null;
  /** vehicles.legal_owner. Null, or 'jasr', means it is ours. */
  owner?: string | null;
  /** Drivers only: the plate they currently hold, if any. */
  vehiclePlate?: string | null;
  /** Vehicles only: who is on it now. */
  driverId?: string | null;
  driverName?: string | null;
}

// Same tags the wall uses. Two screens describing one truck differently is how
// people stop trusting both.
const OWNER_TAG: Record<string, string> = { asian_kings: 'AK' };
/** The same owners written out, for the column. Anything unmapped shows its
 *  raw value rather than being hidden, so a new owner appears the day it does. */
const OWNER_NAME: Record<string, string> = { jasr: 'JASR', asian_kings: 'Asian Kings' };
/** What the Owner dropdown offers. Matches the enum the API validates against;
 *  adding an owner means adding it in both, on purpose. */
const OWNER_CHOICES = ['jasr', 'asian_kings'] as const;

/**
 * What the combined drivers+vehicles download contains, chosen by the owner of
 * the business rather than derived from the table.
 *
 * Project and Investor are the NAME columns: read-only display twins of the
 * code columns, because only `code` carries a unique constraint and so only the
 * code can safely match on re-upload. This file is for reading.
 */
/**
 * One row = a driver and the truck they are on. Left half the driver, right half
 * the vehicle, in the order chosen for each.
 *
 * `get` reads from the API record; a missing value becomes '' rather than
 * "undefined" landing in a cell.
 */
type Rec = Record<string, unknown>;
const txt = (v: unknown): string | number => {
  if (v == null) return '';
  if (typeof v === 'number') return v;
  const s = String(v);
  // ISO timestamps read as dates in a sheet nobody wants to widen.
  return /^\d{4}-\d{2}-\d{2}T/.test(s) ? s.slice(0, 10) : s;
};

const PAIR_COLUMNS: Array<{
  header: string;
  from: 'driver' | 'vehicle';
  /** Ticked when the drawer first opens. The rest are there when wanted. */
  dflt?: boolean;
  get: (r: Rec, sideOf: (id: unknown) => string) => string | number;
}> = [
  // ---------------------------------------------------------------- driver
  { header: 'Driver code',            from: 'driver', get: (d) => txt(d.code) },
  { header: 'Full name EN',           from: 'driver', dflt: true, get: (d) => txt(d.fullNameEn) },
  { header: 'Full name AR',           from: 'driver', get: (d) => txt(d.fullNameAr) },
  { header: 'Full name UR',           from: 'driver', get: (d) => txt(d.fullNameUr) },
  { header: 'Driver side',            from: 'driver', dflt: true, get: (d, side) => side(d.projectId) },
  { header: 'Nationality',            from: 'driver', dflt: true, get: (d) => txt(d.countryOfOrigin) },
  { header: 'Date of birth',          from: 'driver', dflt: true, get: (d) => txt(d.dateOfBirth) },
  { header: 'Blood type',             from: 'driver', dflt: true, get: (d) => txt(d.bloodType) },
  { header: 'Phone',                  from: 'driver', dflt: true, get: (d) => txt(d.phone) },
  { header: 'WhatsApp',               from: 'driver', get: (d) => txt(d.whatsapp) },
  { header: 'Emergency contact name', from: 'driver', get: (d) => txt(d.emergencyContactName) },
  { header: 'Emergency contact phone',from: 'driver', get: (d) => txt(d.emergencyContactPhone) },
  { header: 'Iqama number',           from: 'driver', dflt: true, get: (d) => txt(d.iqamaNumber) },
  { header: 'Iqama expiry',           from: 'driver', dflt: true, get: (d) => txt(d.iqamaExpiry) },
  { header: 'License number',         from: 'driver', get: (d) => txt(d.licenseNumber) },
  { header: 'License category',       from: 'driver', get: (d) => txt(d.licenseCategory) },
  { header: 'License expiry',         from: 'driver', dflt: true, get: (d) => txt(d.licenseExpiry) },
  { header: 'License issue date',     from: 'driver', dflt: true, get: (d) => txt(d.licenseIssueDate) },
  { header: 'Passport number',        from: 'driver', get: (d) => txt(d.passportNumber) },
  { header: 'Passport expiry',        from: 'driver', get: (d) => txt(d.passportExpiry) },
  { header: 'Passport country',       from: 'driver', get: (d) => txt(d.passportCountry) },
  { header: 'Medical cert expiry',    from: 'driver', get: (d) => txt(d.medicalCertExpiry) },
  { header: 'Legal entity',           from: 'driver', get: (d) => txt((d.legalEntity as Rec | null)?.nameEn) },
  { header: 'Pay model',              from: 'driver', get: (d) => txt(d.payModel) },
  { header: 'Base salary SAR',        from: 'driver', get: (d) => txt(d.baseSalarySar) },
  { header: 'Monthly trip target',    from: 'driver', get: (d) => txt(d.monthlyTripTarget) },
  { header: 'Bonus per trip SAR',     from: 'driver', get: (d) => txt(d.bonusPerTripSar) },
  { header: 'Bank name',              from: 'driver', dflt: true, get: (d) => txt(d.bankName) },
  { header: 'IBAN',                   from: 'driver', dflt: true, get: (d) => txt(d.iban) },
  { header: 'Account holder',         from: 'driver', dflt: true, get: (d) => txt(d.accountHolder) },
  { header: 'Hire date',              from: 'driver', get: (d) => txt(d.hireDate) },
  { header: 'End date',               from: 'driver', get: (d) => txt(d.endDate) },
  { header: 'Driver status',          from: 'driver', get: (d) => txt(d.status) },
  { header: 'Driver notes',           from: 'driver', get: (d) => txt(d.notes) },

  // --------------------------------------------------------------- vehicle
  { header: 'Vehicle code',           from: 'vehicle', get: (v) => txt(v.code) },
  { header: 'Plate',                  from: 'vehicle', dflt: true, get: (v) => txt(v.plateEn) },
  { header: 'Plate AR',               from: 'vehicle', get: (v) => txt(v.plateAr) },
  { header: 'Plate type',             from: 'vehicle', get: (v) => txt(v.plateType) },
  { header: 'Vehicle type',           from: 'vehicle', dflt: true, get: (v) => txt(v.vehicleType) },
  { header: 'Owner',                  from: 'vehicle', get: (v) => txt(v.legalOwner) },
  { header: 'Investor name',          from: 'vehicle', dflt: true, get: (v) => txt((v.investor as Rec | null)?.fullNameEn) },
  { header: 'Vehicle legal entity',   from: 'vehicle', get: (v) => txt((v.legalEntity as Rec | null)?.nameEn) },
  { header: 'Vehicle side',           from: 'vehicle', dflt: true, get: (v, side) => side(v.projectId) },
  { header: 'Brand',                  from: 'vehicle', dflt: true, get: (v) => txt(v.brand) },
  { header: 'Model',                  from: 'vehicle', dflt: true, get: (v) => txt(v.model) },
  { header: 'Year',                   from: 'vehicle', dflt: true, get: (v) => txt(v.year) },
  { header: 'Status',                 from: 'vehicle', dflt: true, get: (v) => txt(v.status) },
  { header: 'Colour',                 from: 'vehicle', dflt: true, get: (v) => txt(v.colorOfficial) },
  { header: 'VIN',                    from: 'vehicle', get: (v) => txt(v.vin) },
  { header: 'Engine number',          from: 'vehicle', get: (v) => txt(v.engineNumber) },
  { header: 'Fuel type',              from: 'vehicle', get: (v) => txt(v.fuelType) },
  { header: 'Transmission',           from: 'vehicle', get: (v) => txt(v.transmission) },
  { header: 'Axles',                  from: 'vehicle', get: (v) => txt(v.axlesCount) },
  { header: 'GVWR kg',                from: 'vehicle', get: (v) => txt(v.gvwrKg) },
  { header: 'Payload capacity kg',    from: 'vehicle', get: (v) => txt(v.payloadCapacityKg) },
  { header: 'Manufacturing country',  from: 'vehicle', get: (v) => txt(v.manufacturingCountry) },
  { header: 'Odometer km',            from: 'vehicle', get: (v) => txt(v.currentOdometerKm) },
  { header: 'Target tonnage m3',      from: 'vehicle', get: (v) => txt(v.targetTonnageM3) },
  { header: 'Istimara number',        from: 'vehicle', dflt: true, get: (v) => txt(v.istimaraNumber) },
  { header: 'Istimara expiry',        from: 'vehicle', dflt: true, get: (v) => txt(v.istimaraExpiry) },
  { header: 'Insurance company',      from: 'vehicle', dflt: true, get: (v) => txt(v.insuranceCompanyName) },
  { header: 'Insurance policy number',from: 'vehicle', dflt: true, get: (v) => txt(v.insurancePolicyNumber) },
  { header: 'Insurance expiry',       from: 'vehicle', dflt: true, get: (v) => txt(v.insuranceExpiry) },
  { header: 'Naqliat card number',    from: 'vehicle', get: (v) => txt(v.naqliatCardNumber) },
  { header: 'Naqliat scope',          from: 'vehicle', get: (v) => txt(v.naqliatScope) },
  { header: 'Naqliat issue date',     from: 'vehicle', get: (v) => txt(v.naqliatCardIssueDate) },
  { header: 'Naqliat expiry',         from: 'vehicle', get: (v) => txt(v.naqliatCardExpiryDate) },
  { header: 'Inspection certificate', from: 'vehicle', get: (v) => txt(v.inspectionCertificateNumber) },
  { header: 'Inspection station',     from: 'vehicle', get: (v) => txt(v.inspectionStationName) },
  { header: 'Inspection last date',   from: 'vehicle', dflt: true, get: (v) => txt(v.inspectionLastDate) },
  { header: 'Inspection next due',    from: 'vehicle', get: (v) => txt(v.inspectionNextDueDate) },
  { header: 'Inspection status',      from: 'vehicle', dflt: true, get: (v) => txt(v.inspectionStatus) },
  { header: 'Afaqy device id',        from: 'vehicle', get: (v) => txt(v.afaqyDeviceId) },
  { header: 'PetroApp card id',       from: 'vehicle', get: (v) => txt(v.petroappCardId) },
  { header: 'Purchase date',          from: 'vehicle', get: (v) => txt(v.purchaseDate) },
  { header: 'Purchase price SAR',     from: 'vehicle', get: (v) => txt(v.purchasePriceSar) },
  { header: 'Vehicle notes',          from: 'vehicle', get: (v) => txt(v.notes) },
];

const ownerName = (o?: string | null) => (o ? (OWNER_NAME[o] ?? o) : null);
const ownerTag = (o?: string | null) =>
  (o && o !== 'jasr' ? (OWNER_TAG[o] ?? o.slice(0, 3).toUpperCase()) : '');

interface ProjectRow { id: string; code: string; name_en: string }

// listVehicles clamps pageSize to 100 server-side whatever is asked for, so
// asking for more would quietly return 100 and the footer would under-report
// how much is hidden. Ask for what we can actually get.
const PAGE_SIZE = 100;

async function fetchProjects(): Promise<ProjectRow[]> {
  const { data } = await apiClient.get<{ items: ProjectRow[] }>('/api/v1/projects');
  return data.items ?? [];
}

interface Page { rows: Row[]; total: number }

async function fetchRows(kind: Kind, scope: string, search: string): Promise<Page> {
  const params: Record<string, string | number> = { pageSize: PAGE_SIZE };
  // ALWAYS send a scope, including 'all'.
  //
  // Both list routes fall back to the SPA's X-Project-Id header when no
  // projectId and no projectScope are passed, and the axios interceptor puts
  // that header on every request. Omitting the parameter therefore did not mean
  // "no filter" - it meant "whichever project box the operator happens to be
  // in", so All sides quietly showed one side. Sending 'all' explicitly is
  // treated as no filter by the services AND suppresses the header fallback.
  params.projectScope = scope;
  if (search.trim()) params.search = search.trim();

  if (kind === 'drivers') {
    const { data } = await apiClient.get<{ items: Array<Record<string, unknown>>; total: number }>(
      '/api/v1/drivers', { params },
    );
    return {
      total: data.total ?? 0,
      rows: (data.items ?? []).map((d) => ({
        id: String(d.id),
        code: String(d.code ?? ''),
        name: String(d.fullNameEn ?? ''),
        sub: (d.iqamaNumber as string) ?? null,
        projectId: (d.projectId as string) ?? null,
        // The list already carries the current assignment; no second request.
        vehiclePlate: ((d.currentVehicles as Array<{ plateEn?: string }> | undefined)?.[0]?.plateEn) ?? null,
        // A driver's own legal entity is identical for all 87, so it would be a
        // column of one repeated word. Their truck's owner is the useful one.
        owner: ((d.currentVehicles as Array<{ legalOwner?: string }> | undefined)?.[0]?.legalOwner) ?? null,
      })),
    };
  }
  const { data } = await apiClient.get<{ items: Array<Record<string, unknown>>; total: number }>(
    '/api/v1/vehicles', { params },
  );
  return {
    total: data.total ?? 0,
    rows: (data.items ?? []).map((v) => ({
      id: String(v.id),
      code: String(v.code ?? ''),
      name: String(v.plateEn ?? v.plate ?? ''),
      sub: (v.vehicleType as string) ?? null,
      projectId: (v.projectId as string) ?? null,
      owner: (v.legalOwner as string | null) ?? null,
      // The list already carries the current holder; no second request.
      driverId: ((v.currentDriver as { id?: string } | undefined)?.id) ?? null,
      driverName: ((v.currentDriver as { fullNameEn?: string } | undefined)?.fullNameEn) ?? null,
    })),
  };
}

export function SidesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [kind, setKind] = useState<Kind>('drivers');
  const [scope, setScope] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveTo, setMoveTo] = useState<string>('');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [chosenCols, setChosenCols] = useState<Set<string>>(new Set());
  // Which question the picker is answering: the ticked rows, or the whole side.
  const [exportMode, setExportMode] = useState<'side' | 'selection'>('side');
  // Which row is mid-move, so its dropdown can show it is working without
  // freezing the whole table.
  const [movingId, setMovingId] = useState<string | null>(null);

  const { user } = useAuth();
  const canDelete = user?.role === 'super_admin' || user?.role === 'admin';

  const { data: projects } = useQuery({
    queryKey: ['operational-projects'],
    queryFn: fetchProjects,
    staleTime: 10 * 60 * 1000,
  });

  // Records that contradict each other. Read-only: both lists are judgements
  // about people and money, and neither is safe to settle from a query.
  const { data: mismatches } = useQuery({
    queryKey: ['side-mismatches'],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        sideMismatches: Array<{ driverId: string; driverCode: string; driverName: string;
                                driverSide: string; plate: string; truckSide: string }>;
        fuelOnOtherTruck: Array<{ driverId: string; driverCode: string; driverName: string;
                                  assignedPlate: string; fuelledPlate: string;
                                  fills: number; sar: number }>;
        summary: { sideMismatches: number; fuelOnOtherTruck: number; affected: number };
      }>('/api/v1/drivers/side-mismatches');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const [mmOpen, setMmOpen] = useState(false);

  // Trucks that can be handed to a driver, and the in-flight state of a change.
  const [vehicleOptions, setVehicleOptions] = useState<PickableVehicle[]>([]);
  const [assignBusy, setAssignBusy] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<Record<string, string>>({});
  const canAssign = CAN_ASSIGN.includes(user?.role ?? '');

  const [driverOptions, setDriverOptions] = useState<PickableDriver[]>([]);

  useEffect(() => {
    if (kind !== 'vehicles' || !canAssign) return;
    apiClient
      .get<{ items: PickableDriver[] }>('/api/v1/driver-vehicle-assignments/pickable-drivers')
      .then((r) => setDriverOptions(r.data.items ?? []))
      .catch(() => setDriverOptions([]));
  }, [kind, canAssign]);

  useEffect(() => {
    if (kind !== 'drivers' || !canAssign) return;
    apiClient
      .get<{ items: PickableVehicle[] }>('/api/v1/driver-vehicle-assignments/pickable-vehicles')
      .then((r) => setVehicleOptions(r.data.items ?? []))
      .catch(() => setVehicleOptions([]));
  }, [kind, canAssign]);

  /**
   * Assign or clear, then refresh BOTH the roster and the picker list. Assigning
   * a truck takes it off whoever held it, so a stale picker would keep offering
   * it as free and the next person would think they were the first to take it.
   */
  const runAssign = async (driverId: string, fn: () => Promise<{ data: unknown }>) => {
    setAssignBusy(driverId);
    setAssignError((p) => ({ ...p, [driverId]: '' }));
    try {
      const res = await fn();
      // The server says when it closed somebody else's assignment. Passing that
      // on matters: reassigning quietly takes a truck away from another driver.
      const warnings = (res.data as { warnings?: string[] })?.warnings ?? [];
      if (warnings.length) setNote(warnings.join(' '));
      await qc.invalidateQueries({ queryKey: ['sides-roster'] });
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      // Both pickers, because one assignment changes both lists: a truck stops
      // being free AND a driver stops being free, and a stale list on either
      // tab would keep offering what has just been taken.
      const [vs, ds] = await Promise.all([
        apiClient.get<{ items: PickableVehicle[] }>('/api/v1/driver-vehicle-assignments/pickable-vehicles'),
        apiClient.get<{ items: PickableDriver[] }>('/api/v1/driver-vehicle-assignments/pickable-drivers'),
      ]);
      setVehicleOptions(vs.data.items ?? []);
      setDriverOptions(ds.data.items ?? []);
    } catch (e) {
      setAssignError((p) => ({ ...p, [driverId]: extractErrorMessage(e) }));
    } finally {
      setAssignBusy(null);
    }
  };

  /**
   * Take a flagged row to the driver it is about: switch to the drivers tab and
   * filter to that code. The code is unique and the list already searches it,
   * so the driver appears whatever was on screen before.
   */
  const revealDriver = (code: string) => {
    setKind('drivers');
    setSelected(new Set());
    setMoveTo('');
    setSearch(code);
    setMmOpen(false);
  };

  // The sheet's real headings, from the registry - not the four this table
  // happens to show.
  const { data: sheetCols } = useQuery({
    queryKey: ['excel-columns', kind],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: Array<{ header: string; required: boolean }> }>(
        `/api/v1/excel/${kind}/columns`,
      );
      return data.items;
    },
    staleTime: 60 * 60 * 1000,
  });

  // This month's fuel per vehicle. Only for the vehicles tab - drivers do not
  // hold a card, so asking would return nothing useful.
  const { data: fuelByVehicle } = useQuery({
    queryKey: ['fuel-by-vehicle-month'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: Array<{ vehicleId: string; fills: number; liters: number; sar: number }> }>(
        '/api/v1/fuel-by-vehicle-month',
      );
      return new Map(data.items.map((r) => [r.vehicleId, r]));
    },
    enabled: kind === 'vehicles',
    staleTime: 5 * 60 * 1000,
  });

  const { data: page, isLoading } = useQuery({
    queryKey: ['sides-roster', kind, scope, search],
    queryFn: () => fetchRows(kind, scope, search),
    staleTime: 15 * 1000,
  });

  // Project id -> the label shown on a row, so a list can name a side without
  // every row carrying the project object.
  const sideNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects ?? []) {
      const known = SIDES.find((s) => s.code === p.code);
      m.set(p.id, known ? known.label : p.name_en);
    }
    return m;
  }, [projects]);

  const idForSideKey = (key: string): string | null => {
    const side = SIDES.find((s) => s.key === key);
    if (!side) return null;
    return (projects ?? []).find((p) => p.code === side.code)?.id ?? null;
  };

  const move = useMutation({
    mutationFn: async () => {
      const ids = [...selected];
      const projectId = moveTo === 'none' ? null : idForSideKey(moveTo);
      if (moveTo !== 'none' && !projectId) throw new Error('That side could not be resolved');
      const url = kind === 'drivers' ? '/api/v1/drivers/bulk-project' : '/api/v1/vehicles/bulk-project';
      const { data } = await apiClient.post(url, { ids, projectId });
      return data as { moved: number };
    },
    onSuccess: (d) => {
      const where = moveTo === 'none' ? 'no side' : SIDES.find((s) => s.key === moveTo)?.label;
      setNote(`Moved ${d.moved} ${kind === 'drivers' ? 'driver' : 'vehicle'}${d.moved === 1 ? '' : 's'} to ${where}.`);
      setError(null);
      setSelected(new Set());
      setMoveTo('');
      void qc.invalidateQueries({ queryKey: ['sides-roster'] });
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (e) => { setError(extractErrorMessage(e)); setNote(null); },
  });

  /** Open the picker with everything ticked - the common case is the whole sheet. */
  const openExport = (mode: 'side' | 'selection') => {
    setExportMode(mode);
    setChosenCols(new Set((sheetCols ?? []).map((c) => c.header)));
    setExportOpen(true);
  };

  /**
   * Export, with only the chosen columns, over whichever scope opened the
   * picker: the ticked rows or the whole side. One path for both - two code
   * paths producing the same file is how they drift apart, one gaining a fix
   * the other never gets.
   *
   * Repeated `columns=` parameters rather than one comma-joined string: no
   * heading contains a comma today, but sheets are edited by people and one
   * eventually will, and a heading silently splitting in two is a poor way to
   * discover that.
   */
  /**
   * Both rosters in one workbook, one sheet each.
   *
   * Two calls to the same export the per-tab button uses, then the sheets are
   * copied into a single file. A dedicated endpoint would be a second way to
   * build the same thing, and the two would eventually disagree.
   */
  const [bothBusy, setBothBusy] = useState(false);
  const [bothOpen, setBothOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  /** The row someone pressed, shown in full beside the list. */
  const [detail, setDetail] = useState<Row | null>(null);
  // Everything ticked to begin with - what the button did before it asked.
  const [bothCols, setBothCols] = useState<Set<string>>(
    () => new Set(PAIR_COLUMNS.filter((c) => c.dflt).map((c) => c.header)),
  );
  const exportBoth = async () => {
    setBothBusy(true);
    setError(null);
    try {
      // Every row, not the page on screen. pageSize is clamped to 100 server
      // side whatever is asked for, so this pages until a short page arrives.
      const all = async (entity: 'drivers' | 'vehicles') => {
        const out: Array<Record<string, unknown>> = [];
        for (let page = 1; page <= 50; page++) {
          const qs = new URLSearchParams({ page: String(page), pageSize: '100' });
          if (scope && scope !== 'all') qs.set('projectScope', scope);
          const { data } = await apiClient.get<{ items?: Array<Record<string, unknown>> }>(
            `/api/v1/${entity}?${qs.toString()}`,
          );
          const items = data.items ?? [];
          out.push(...items);
          if (items.length < 100) break;
        }
        return out;
      };

      const [drivers, vehicles] = await Promise.all([all('drivers'), all('vehicles')]);
      const sideOf = (id: unknown) =>
        (projects ?? []).find((p) => p.id === String(id ?? ''))?.name_en ?? '';

      // Paired on the vehicle's current driver - the link the database holds -
      // rather than on a plate string, which has already been written three
      // different ways in this system.
      const byDriverId = new Map<string, Record<string, unknown>>();
      for (const v of vehicles) {
        const did = (v.currentDriverId ?? (v.currentDriver as Record<string, unknown> | null)?.id) as string | undefined;
        if (did) byDriverId.set(String(did), v);
      }

      // Fixed order, not the order they were ticked: a sheet whose columns
      // move between exports cannot be compared with last month's.
      const cols = PAIR_COLUMNS.filter((c) => bothCols.has(c.header));
      const rows: Array<Array<string | number>> = [cols.map((c) => c.header)];
      const usedVehicles = new Set<string>();
      for (const d of drivers) {
        const v = byDriverId.get(String(d.id)) ?? null;
        if (v) usedVehicles.add(String(v.id));
        rows.push(cols.map((c) =>
          c.from === 'driver' ? c.get(d, sideOf) : (v ? c.get(v, sideOf) : '')));
      }
      // Trucks nobody is on still appear, with the driver half blank. Leaving
      // them out would hide exactly the rows worth finding.
      for (const v of vehicles) {
        if (usedVehicles.has(String(v.id))) continue;
        rows.push(cols.map((c) =>
          c.from === 'vehicle' ? c.get(v, sideOf) : ''));
      }

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = rows[0].map((h) => ({ wch: Math.max(String(h).length + 2, 12) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Drivers & vehicles');
      XLSX.writeFile(wb, `jasrhub-drivers-and-vehicles-${scope === 'all' ? 'all-sides' : scope}.xlsx`);
      setBothOpen(false);
      setNote(`Exported ${rows.length - 1} row(s) and ${cols.length} column(s) for ${scope === 'all' ? 'all sides' : scope} in one sheet.`);
    } catch (e) {
      setError(extractErrorMessage(e));
      setNote(null);
    } finally {
      setBothBusy(false);
    }
  };

  const exportColumns = async () => {
    setExporting(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      for (const h of chosenCols) qs.append('columns', h);
      if (exportMode === 'selection') qs.append('ids', [...selected].join(','));
      else qs.append('projectScope', scope);
      const { data } = await apiClient.get(`/api/v1/excel/${kind}/export?${qs.toString()}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportMode === 'selection'
        ? `jasrhub-${kind}-selection-${selected.size}.xlsx`
        : `jasrhub-${kind}-${scope}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setNote(exportMode === 'selection'
        ? `Exported ${selected.size} selected ${kind} with ${chosenCols.size} columns.`
        : `Exported ${kind} for ${scope === 'all' ? 'all sides' : scope} with ${chosenCols.size} columns.`);
      setExportOpen(false);
    } catch (e) {
      setError(extractErrorMessage(e));
      setNote(null);
    } finally {
      setExporting(false);
    }
  };

  /**
   * Create, filing it under whichever side is being viewed.
   *
   * The side is applied to the PAYLOAD rather than pre-filled into the form:
   * both forms read several fields straight off `initial` with no fallback, so
   * a partial object handed in to carry a project id throws on render. An
   * explicit choice inside the form still wins - only an empty one inherits
   * the current filter.
   */
  const createHere = async (input: Record<string, unknown>) => {
    const viewing = scope !== 'all' && scope !== 'none' ? idForSideKey(scope) : null;
    const projectId = (input.projectId as string | null | undefined) ?? viewing;
    const url = kind === 'drivers' ? '/api/v1/drivers' : '/api/v1/vehicles';
    await apiClient.post(url, { ...input, projectId });
    const side = projectId ? (sideNameById.get(projectId) ?? 'a side') : 'no side';
    setNote(`${kind === 'drivers' ? 'Driver' : 'Vehicle'} created under ${side}.`);
    setError(null);
    setAdding(false);
    await qc.invalidateQueries({ queryKey: ['sides-roster'] });
    void qc.invalidateQueries({ queryKey: ['drivers'] });
    void qc.invalidateQueries({ queryKey: ['vehicles'] });
  };

  /** Move one row. Selecting-then-choosing is right for thirty and three steps
   *  too many for one, so a row's own dropdown does it directly. */
  // Change a truck's legal owner. Same shape as rowMove: one row at a time,
  // no selection, and the list refreshes from the server rather than being
  // patched locally - so what is on screen is what was actually saved.
  const [ownerBusy, setOwnerBusy] = useState<string | null>(null);
  const setOwner = async (row: Row, owner: string) => {
    if (!owner || owner === row.owner) return;
    setOwnerBusy(row.id);
    setNote(null);
    try {
      await apiClient.patch(`/api/v1/vehicles/${row.id}`, { legalOwner: owner });
      setNote(`${row.name} is now owned by ${OWNER_NAME[owner] ?? owner}.`);
      await qc.invalidateQueries({ queryKey: ['sides-roster'] });
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
    } catch (e) {
      setError(extractErrorMessage(e));
      setNote(null);
    } finally {
      setOwnerBusy(null);
    }
  };

  const rowMove = async (row: Row, sideKey: string) => {
    const projectId = sideKey === 'none' ? null : idForSideKey(sideKey);
    if (sideKey !== 'none' && !projectId) { setError('That side could not be resolved'); return; }
    setMovingId(row.id);
    setError(null);
    try {
      const url = kind === 'drivers' ? '/api/v1/drivers/bulk-project' : '/api/v1/vehicles/bulk-project';
      await apiClient.post(url, { ids: [row.id], projectId });
      const where = sideKey === 'none' ? 'no side' : SIDES.find((x) => x.key === sideKey)?.label;
      setNote(`${row.name || row.code} moved to ${where}.`);
      await qc.invalidateQueries({ queryKey: ['sides-roster'] });
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
    } catch (e) {
      setError(extractErrorMessage(e));
      setNote(null);
    } finally {
      setMovingId(null);
    }
  };

  /** The side key a row currently sits on, for its dropdown's value. */
  const sideKeyOf = (row: Row): string => {
    if (!row.projectId) return 'none';
    const proj = (projects ?? []).find((p) => p.id === row.projectId);
    return SIDES.find((x) => x.code === proj?.code)?.key ?? '';
  };

  const list = page?.rows ?? [];
  const total = page?.total ?? 0;
  const allShown = list.length > 0 && list.every((r) => selected.has(r.id));

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Switching tab or filter clears the selection: rows you can no longer see
  // must not be moved by a button that now reads as being about this list.
  const switchKind = (k: Kind) => { setKind(k); setSelected(new Set()); setMoveTo(''); };
  const switchScope = (s: string) => { setScope(s); setSelected(new Set()); setMoveTo(''); };

  const TABS: Array<{ k: Kind; label: string; icon: typeof UserCog }> = [
    { k: 'drivers', label: 'Drivers', icon: UserCog },
    { k: 'vehicles', label: 'Vehicles', icon: Truck },
  ];

  const FILTERS = [
    { key: 'all', label: 'All sides' },
    ...SIDES.map((s) => ({ key: s.key, label: s.label })),
    { key: 'none', label: 'No side' },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <Link to="/dashboard/settings" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="h-3 w-3" />
          {t('common.back', 'Back')}
        </Link>
        <div className="flex items-center gap-3">
          <FolderInput className="h-7 w-7 text-gold" />
          <h1 className="font-display text-3xl text-foreground">Sides</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          Which side each driver and truck is filed under — and move them between sides.
        </p>

        {/* Records that disagree with each other. Amber, not rose: none of this is
            necessarily wrong - a driver may genuinely be paid by one side while
            driving another's truck - so the claim is the narrow, defensible one:
            these two records disagree, someone should look. */}
        {mismatches && mismatches.summary.affected > 0 && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {mismatches.summary.affected} record{mismatches.summary.affected === 1 ? '' : 's'} disagree with each other
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {mismatches.summary.sideMismatches > 0 && (
                    <>{mismatches.summary.sideMismatches} driver{mismatches.summary.sideMismatches === 1 ? '' : 's'} on a different side from the truck they drive</>
                  )}
                  {mismatches.summary.sideMismatches > 0 && mismatches.summary.fuelOnOtherTruck > 0 && ' · '}
                  {mismatches.summary.fuelOnOtherTruck > 0 && (
                    <>{mismatches.summary.fuelOnOtherTruck} fuelling a truck that is not the one they are assigned to</>
                  )}
                  . Nothing has been changed.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMmOpen((v) => !v)}
                className="shrink-0 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                {mmOpen ? 'Hide' : 'Show'}
                <ChevronDown className={`h-3 w-3 transition-transform ${mmOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {mmOpen && (
              <div className="mt-4 space-y-4 ps-8">
                {mismatches.sideMismatches.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      Driver and truck on different sides
                    </p>
                    <div className="space-y-1">
                      {mismatches.sideMismatches.map((m) => (
                        <button
                          key={`s-${m.driverId}`}
                          type="button"
                          onClick={() => revealDriver(m.driverCode)}
                          className="w-full text-start flex flex-wrap items-center gap-2 text-xs hover:bg-amber-500/5 rounded px-2 py-1 -mx-2"
                        >
                          <span className="font-mono text-muted-foreground">{m.driverCode}</span>
                          <span className="text-foreground">{m.driverName}</span>
                          <span className="text-muted-foreground">
                            filed on <b className="text-foreground">{m.driverSide}</b>, drives{' '}
                            <b className="text-foreground">{m.plate}</b> on{' '}
                            <b className="text-foreground">{m.truckSide}</b>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {mismatches.fuelOnOtherTruck.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      Fuel recorded against a different truck
                      <span className="font-normal text-muted-foreground"> · last 90 days, 3+ fills</span>
                    </p>
                    <div className="space-y-1">
                      {mismatches.fuelOnOtherTruck.map((m) => (
                        <button
                          key={`f-${m.driverId}-${m.fuelledPlate}`}
                          type="button"
                          onClick={() => revealDriver(m.driverCode)}
                          className="w-full text-start flex flex-wrap items-center gap-2 text-xs hover:bg-amber-500/5 rounded px-2 py-1 -mx-2"
                        >
                          <span className="font-mono text-muted-foreground">{m.driverCode}</span>
                          <span className="text-foreground">{m.driverName}</span>
                          <span className="text-muted-foreground">
                            drives <b className="text-foreground">{m.assignedPlate}</b>, fuels{' '}
                            <b className="text-amber-600 dark:text-amber-400">{m.fuelledPlate}</b>
                          </span>
                          <span className="ms-auto text-muted-foreground">
                            {m.fills} fills · {m.sar.toLocaleString()} SAR
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Drivers / Vehicles */}
        <div className="flex gap-2 mb-4 items-center">
          {TABS.map((tab) => (
            <button
              key={tab.k}
              type="button"
              onClick={() => switchKind(tab.k)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                kind === tab.k
                  ? 'border-gold bg-gold/10 text-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}

          {/* Adds to whichever side is being viewed, so filtering to NEOM and
              pressing Add does the obvious thing. */}
          <Button variant="outline" className="ms-auto gap-2" onClick={() => setBothOpen(true)} disabled={bothBusy}
            title="One sheet: each driver beside the truck they are on">
            {bothBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export both
          </Button>
          {/* Bringing a list in sits beside taking one out. */}
          <Button variant="outline" className="gap-2" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" /> Import
          </Button>
          <Button variant="gold" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            {kind === 'drivers' ? 'Add driver' : 'Add vehicle'}
          </Button>
        </div>

        {/* Side filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => switchScope(f.key)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                scope === f.key
                  ? 'border-gold bg-gold/10 text-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={kind === 'drivers' ? 'Search name, code, iqama…' : 'Search plate or code…'}
            className="ps-10"
          />
        </div>

        {note && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            {note}
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm">{error}</div>
        )}

        {/* The move bar only exists while something is selected - a control that
            cannot do anything is a control that has to be explained. */}
        {selected.size > 0 && (
          <div className="mb-4 rounded-lg border border-gold/40 bg-gold/5 p-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <select
              value={moveTo}
              onChange={(e) => setMoveTo(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm"
            >
              <option value="">Move to…</option>
              {SIDES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              <option value="none">No side</option>
            </select>
            <Button
              variant="gold"
              disabled={!moveTo || move.isPending}
              onClick={() => move.mutate()}
            >
              {move.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Move'}
            </Button>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => { setSelected(new Set()); setMoveTo(''); }}
            >
              Clear
            </button>

            {/* Right-hand end: export is what you do INSTEAD of moving, not a
                modifier of it, so it sits apart from the move controls. */}
            <Button
              variant="outline"
              className="ms-auto"
              onClick={() => openExport('selection')}
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        )}

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="w-10 p-3">
                  <input
                    type="checkbox"
                    checked={allShown}
                    onChange={() => setSelected(allShown ? new Set() : new Set(list.map((r) => r.id)))}
                    aria-label="Select all shown"
                  />
                </th>
                <th className="text-start p-3 font-medium">Code</th>
                <th className="text-start p-3 font-medium">{kind === 'drivers' ? 'Name' : 'Plate'}</th>
                <th className="text-start p-3 font-medium">{kind === 'drivers' ? 'Iqama' : 'Type'}</th>
                {kind === 'drivers' && (
                  <th className="text-start p-3 font-medium">Vehicle</th>
                )}
                {kind === 'vehicles' && (
                  <th className="text-start p-3 font-medium">Driver</th>
                )}
                {kind === 'vehicles' && (
                  <th className="text-start p-3 font-medium">Fuel this month</th>
                )}
                <th className="text-start p-3 font-medium">Owner</th>
                <th className="text-start p-3 font-medium">Side</th>
                <th className="w-16 p-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                </td></tr>
              )}
              {!isLoading && list.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">
                  Nothing here.
                </td></tr>
              )}
              {list.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setDetail(r)}
                  className={`border-t border-border cursor-pointer hover:bg-muted/30 ${
                    selected.has(r.id) ? 'bg-gold/5' : ''
                  }`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${r.code}`}
                    />
                  </td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{r.code}</td>
                  <td className="p-3 text-foreground">
                    {r.name}
                  </td>
                  <td className="p-3 text-muted-foreground">{r.sub ?? '—'}</td>
                  {kind === 'drivers' && (
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <VehicleCell
                        driverId={r.id}
                        plate={r.vehiclePlate ?? null}
                        canAssign={canAssign}
                        vehicles={vehicleOptions}
                        onAssign={(driverId, vehicleId) => void runAssign(driverId, () =>
                          apiClient.post('/api/v1/driver-vehicle-assignments', { driverId, vehicleId }))}
                        onUnassign={(driverId) => void runAssign(driverId, () =>
                          apiClient.post('/api/v1/driver-vehicle-assignments/unassign', { driverId }))}
                        busy={assignBusy === r.id}
                        error={assignError[r.id] || null}
                      />
                    </td>
                  )}
                  {kind === 'vehicles' && (
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <DriverCell
                        vehicleId={r.id}
                        driverName={r.driverName ?? null}
                        canAssign={canAssign}
                        drivers={driverOptions}
                        onAssign={(vehicleId, driverId) => void runAssign(vehicleId, () =>
                          apiClient.post('/api/v1/driver-vehicle-assignments', { driverId, vehicleId }))}
                        onUnassign={() => { if (r.driverId) void runAssign(r.id, () =>
                          apiClient.post('/api/v1/driver-vehicle-assignments/unassign', { driverId: r.driverId })); }}
                        busy={assignBusy === r.id}
                        error={assignError[r.id] || null}
                      />
                    </td>
                  )}
                  {kind === 'vehicles' && (
                    <td className="p-3 whitespace-nowrap">
                      {(() => {
                        const f = fuelByVehicle?.get(r.id);
                        // Absent means no card is reporting for this truck,
                        // which is a different thing from a truck that has not
                        // fuelled - so it says so rather than printing 0 L.
                        if (!f) return <span className="text-muted-foreground text-xs">no card data</span>;
                        return (
                          <span className="text-foreground">
                            {f.liters.toLocaleString()} L
                            <span className="text-muted-foreground text-xs">
                              {' · '}{f.sar.toLocaleString()} SAR · {f.fills} fill{f.fills === 1 ? '' : 's'}
                            </span>
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  <td className="p-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {kind === 'vehicles' ? (
                      // Changeable here because the owner belongs to the truck.
                      <select
                        value={r.owner ?? ''}
                        disabled={ownerBusy === r.id}
                        onChange={(e) => void setOwner(r, e.target.value)}
                        aria-label={`Owner of ${r.name}`}
                        className={`h-8 rounded-md border bg-background px-2 text-xs ${
                          r.owner && r.owner !== 'jasr'
                            ? 'border-amber-500/50 text-amber-600 dark:text-amber-400 font-medium'
                            : 'border-border text-foreground'
                        }`}
                      >
                        <option value="" disabled>— not set —</option>
                        {OWNER_CHOICES.map((o) => (
                          <option key={o} value={o}>{OWNER_NAME[o]}</option>
                        ))}
                      </select>
                    ) : (
                      // On a driver row this is the owner of the truck they are
                      // on - the truck's property, not the person's. Changing it
                      // from here would edit a record the row is not about.
                      <span
                        title={r.vehiclePlate ? `Owner of ${r.vehiclePlate}. Change it on the Vehicles tab.` : undefined}
                        className={r.owner && r.owner !== 'jasr'
                          ? 'text-amber-600 dark:text-amber-400 font-medium'
                          : 'text-muted-foreground'}
                      >
                        {ownerName(r.owner) ?? '—'}
                      </span>
                    )}
                    {ownerBusy === r.id && <Loader2 className="h-3 w-3 animate-spin inline ms-2" />}
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    {/* Change it here and it moves. No selection, no second
                        button - the common case is one row at a time. */}
                    <select
                      value={sideKeyOf(r)}
                      disabled={movingId === r.id}
                      onChange={(e) => void rowMove(r, e.target.value)}
                      className={`h-8 rounded-md border bg-background px-2 text-xs ${
                        r.projectId ? 'border-border text-foreground'
                                    : 'border-amber-500/50 text-amber-600 dark:text-amber-400'
                      }`}
                    >
                      {SIDES.map((sd) => <option key={sd.key} value={sd.key}>{sd.label}</option>)}
                      <option value="none">No side</option>
                    </select>
                    {movingId === r.id && <Loader2 className="h-3 w-3 animate-spin inline ms-2" />}
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => setDeleting(r)}
                        className="text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400"
                        aria-label={`Delete ${r.code}`}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > list.length && (
          // Say so rather than let a capped list read as a complete one - and
          // say how many are missing, since "select all shown" only ever means
          // what is on screen.
          <p className="text-xs text-muted-foreground mt-2">
            Showing {list.length} of {total}. Narrow it with the search box or a side filter —
            "select all" only takes the {list.length} listed here.
          </p>
        )}
        <Sheet open={bothOpen} onOpenChange={setBothOpen}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Export drivers and vehicles</SheetTitle>
              <SheetDescription>
                One sheet, each driver beside the truck they are on.{' '}
                {scope === 'all'
                  ? 'Every side.'
                  : scope === 'none'
                    ? 'Only those filed under no side.'
                    : `Only ${SIDES.find((x) => x.key === scope)?.label}.`}
                {' '}Tick the columns you want.
              </SheetDescription>
            </SheetHeader>

            <div className="flex items-center gap-3 my-3 text-xs">
              <Columns3 className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {bothCols.size} of {PAIR_COLUMNS.length} columns
              </span>
              <button type="button" className="text-gold hover:underline"
                onClick={() => setBothCols(new Set(PAIR_COLUMNS.map((c) => c.header)))}>All</button>
              <button type="button" className="text-muted-foreground hover:text-foreground"
                onClick={() => setBothCols(new Set())}>None</button>
              {/* Back to the twenty-nine this started with. With 76 to choose
                  from, "undo my exploring" should not mean re-ticking by hand. */}
              <button type="button" className="text-muted-foreground hover:text-foreground"
                onClick={() => setBothCols(new Set(PAIR_COLUMNS.filter((c) => c.dflt).map((c) => c.header)))}>
                Default
              </button>
            </div>

            {/* Grouped, because the sheet is two things joined. Twenty-nine
                unlabelled boxes would leave you counting to find where the
                driver half ends. */}
            <div className="max-h-[55vh] overflow-y-auto border border-border rounded-lg">
              {(['driver', 'vehicle'] as const).map((half) => {
                const group = PAIR_COLUMNS.filter((c) => c.from === half);
                const allOn = group.every((c) => bothCols.has(c.header));
                return (
                  <div key={half}>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground border-y border-border">
                      <span>{half === 'driver' ? 'Driver' : 'Vehicle'} ({group.filter((c) => bothCols.has(c.header)).length}/{group.length})</span>
                      <button
                        type="button"
                        className="ms-auto text-gold hover:underline normal-case"
                        onClick={() => setBothCols((prev) => {
                          const next = new Set(prev);
                          for (const c of group) { if (allOn) next.delete(c.header); else next.add(c.header); }
                          return next;
                        })}
                      >
                        {allOn ? 'none' : 'all'}
                      </button>
                    </div>
                    {group.map((c) => (
                      <label key={c.header}
                        className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/30 border-b border-border last:border-b-0">
                        <input
                          type="checkbox"
                          checked={bothCols.has(c.header)}
                          onChange={() => setBothCols((prev) => {
                            const next = new Set(prev);
                            if (next.has(c.header)) next.delete(c.header); else next.add(c.header);
                            return next;
                          })}
                        />
                        <span className="text-foreground">{c.header}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground mt-3">
              This sheet is for reading. It pairs two records per row, so it cannot be
              edited and uploaded back — use Export on a tab for that.
            </p>

            <div className="flex gap-2 mt-4">
              <Button variant="gold" disabled={bothBusy || bothCols.size === 0}
                onClick={() => void exportBoth()}>
                {bothBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export {bothCols.size} column{bothCols.size === 1 ? '' : 's'}
              </Button>
              <Button variant="outline" onClick={() => setBothOpen(false)}>Cancel</Button>
            </div>
          </SheetContent>
        </Sheet>

        <Sheet open={exportOpen} onOpenChange={setExportOpen}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>
                Export {kind === 'drivers' ? 'drivers' : 'vehicles'}
              </SheetTitle>
              <SheetDescription>
                {/* Say which rows are going, so a 3-row export and an 87-row one
                    are never confused at the moment of pressing Export. */}
                {exportMode === 'selection'
                  ? `The ${selected.size} row${selected.size === 1 ? '' : 's'} you selected.`
                  : scope === 'all'
                    ? 'Every side.'
                    : scope === 'none'
                      ? 'Only those filed under no side.'
                      : `Only ${SIDES.find((x) => x.key === scope)?.label}.`}
                {' '}Tick the columns you want.
              </SheetDescription>
            </SheetHeader>

            <div className="flex items-center gap-3 my-3 text-xs">
              <Columns3 className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {chosenCols.size} of {(sheetCols ?? []).length} columns
              </span>
              <button
                type="button"
                className="text-gold hover:underline"
                onClick={() => setChosenCols(new Set((sheetCols ?? []).map((c) => c.header)))}
              >
                All
              </button>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setChosenCols(new Set())}
              >
                None
              </button>
            </div>

            <div className="max-h-[55vh] overflow-y-auto border border-border rounded-lg divide-y divide-border">
              {(sheetCols ?? []).map((c) => (
                <label
                  key={c.header}
                  className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/30"
                >
                  <input
                    type="checkbox"
                    checked={chosenCols.has(c.header)}
                    onChange={() => setChosenCols((prev) => {
                      const next = new Set(prev);
                      if (next.has(c.header)) next.delete(c.header); else next.add(c.header);
                      return next;
                    })}
                  />
                  <span className="text-foreground">{c.header}</span>
                  {c.required && (
                    <span className="ms-auto text-[10px] text-muted-foreground">needed to re-upload</span>
                  )}
                </label>
              ))}
            </div>

            {/* Said once, plainly: a sheet missing required headings is a
                report, not something that can be edited and sent back. */}
            <p className="text-xs text-muted-foreground mt-3">
              Leaving out columns marked <em>needed to re-upload</em> makes this a
              read-only report — it cannot be edited and imported back.
            </p>

            <div className="flex gap-2 mt-4">
              <Button
                variant="gold"
                disabled={exporting || chosenCols.size === 0}
                onClick={() => void exportColumns()}
              >
                {exporting
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Download className="h-4 w-4" />}
                Export {chosenCols.size} column{chosenCols.size === 1 ? '' : 's'}
              </Button>
              <Button variant="outline" onClick={() => setExportOpen(false)}>Cancel</Button>
            </div>
          </SheetContent>
        </Sheet>

        {/* The real forms, not a cut-down pair. Creating a driver has one
            definition - required documents, licence category, pay model - and a
            friendlier form here would only be a way to make records the proper
            one would have refused. */}
        <Sheet open={adding} onOpenChange={setAdding}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>{kind === 'drivers' ? 'Add driver' : 'Add vehicle'}</SheetTitle>
              <SheetDescription>
                {scope !== 'all' && scope !== 'none'
                  ? `Filed under ${SIDES.find((x) => x.key === scope)?.label} unless you pick another side below.`
                  : 'Pick a side below, or leave it blank to file it later.'}
              </SheetDescription>
            </SheetHeader>
            {kind === 'drivers' ? (
              <DriverForm
                onSubmit={(input) => createHere(input as unknown as Record<string, unknown>)}
                onCancel={() => setAdding(false)}
                submitLabel="Create driver"
              />
            ) : (
              <VehicleForm
                onSubmit={(input) => createHere(input as unknown as Record<string, unknown>)}
                onCancel={() => setAdding(false)}
                submitLabel="Create vehicle"
              />
            )}
          </SheetContent>
        </Sheet>

        {/* The shared drawer, not a new one: it already requires a reason and a
            typed DELETE, shows the server's dependency counts when a delete is
            refused, and offers force to super_admin. A simpler second dialog
            would only be a worse one. */}
        {deleting && (
          <DeleteConfirmDrawer
            open={!!deleting}
            onOpenChange={(o) => { if (!o) setDeleting(null); }}
            entityKind={kind === 'drivers' ? 'driver' : 'vehicle'}
            label={`${deleting.code} ${deleting.name}`.trim()}
            forceWarning={
              kind === 'drivers'
                ? 'Force-delete removes the driver and unlinks their trips — the trips keep their details with no driver on them.'
                : 'Force-delete removes the vehicle and everything hanging off it. This cannot be undone.'
            }
            performDelete={async ({ force, reason }) => {
              const base = kind === 'drivers' ? '/api/v1/drivers' : '/api/v1/vehicles';
              await apiClient.delete(`${base}/${deleting.id}${force ? '?force=true' : ''}`, {
                data: { reason },
              });
            }}
            onDeleted={() => {
              setNote(`${deleting.name || deleting.code} deleted.`);
              setDeleting(null);
              setSelected((prev) => { const n = new Set(prev); n.delete(deleting.id); return n; });
              void qc.invalidateQueries({ queryKey: ['sides-roster'] });
              void qc.invalidateQueries({ queryKey: ['drivers'] });
              void qc.invalidateQueries({ queryKey: ['vehicles'] });
            }}
          />
        )}

        {detail && (
          <RosterDetail row={detail} kind={kind} onClose={() => setDetail(null)} />
        )}

        {importOpen && (
          <ImportPreviewModal
            onClose={() => setImportOpen(false)}
            onImported={() => setImportOpen(false)}
          />
        )}
      </main>
    </div>
  );
}


/**
 * One driver or vehicle, in full.
 *
 * The roster row carries only what the table needs; this fetches the record
 * itself, so "all details" means the record and not a prettier version of the
 * six columns already on screen. Fields are rendered generically — whatever the
 * API returns is shown — so a column added to drivers or vehicles later appears
 * here without this file being touched.
 */
function RosterDetail({ row, kind, onClose }: {
  row: Row; kind: Kind; onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['roster-detail', kind, row.id],
    queryFn: async () => (await apiClient.get<Record<string, unknown>>(
      `/api/v1/${kind}/${row.id}`)).data,
  });

  /** Machine columns nobody reads, kept out of the way. */
  const HIDE = new Set(['id', 'created_at', 'updated_at', 'createdAt', 'updatedAt',
    'deleted_at', 'password_hash', 'project_id', 'projectId']);

  const pretty = (k: string) =>
    k.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (c) => c.toUpperCase());

  const show = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'yes' : 'no';
    if (typeof v === 'object') return JSON.stringify(v);
    const str = String(v);
    return /^\d{4}-\d{2}-\d{2}T/.test(str) ? str.slice(0, 10) : str;
  };

  const rec = (data && typeof data === 'object'
    ? ((data as Record<string, unknown>).driver ?? (data as Record<string, unknown>).vehicle ?? data)
    : {}) as Record<string, unknown>;
  const entries = Object.entries(rec).filter(([k, v]) =>
    !HIDE.has(k) && typeof v !== 'function');

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground">{row.code}</span>
            <span>{row.name}</span>
          </SheetTitle>
        </SheetHeader>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 p-3 text-sm
                          text-rose-600 dark:text-rose-400 mt-4">
            This record could not be loaded.
          </div>
        )}

        {data && (
          <div className="mt-4 rounded-lg border border-border divide-y divide-border">
            {entries.length === 0 && (
              <div className="px-3 py-3 text-sm text-muted-foreground">Nothing recorded.</div>
            )}
            {entries.map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-3 px-3 py-2">
                <span className="text-xs text-muted-foreground shrink-0">{pretty(k)}</span>
                <span className="text-sm text-end break-words">{show(v)}</span>
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default SidesPage;
